// Analytics user identification wrapper for Clarity and Heap
// Handles both platforms with your existing user data structure

const ANALYTICS_DISABLED = false

export interface UserProperties {
  userId: string
  email?: string
  name?: string
  approvalStatus?:
    | 'not_started'
    | 'in_progress'
    | 'approved'
    | 'rejected'
    | 'pending'
  preferredMarket?: string
  userType?: 'buyer' | 'investor' | 'agent' | 'ops'
  signupDate?: string
  lastActiveDate?: string
  valueShareAmount?: number
  monthlyBudget?: number
  // Allow additional dynamic properties for analytics tracking
  [key: string]: any
}

// Microsoft Clarity global types
declare global {
  interface Window {
    clarity: {
      (
        action: 'identify',
        userId: string,
        sessionId?: string,
        pageId?: string,
        friendlyName?: string
      ): void
      (
        action: 'consent',
        consentData: { analytics_storage: 'granted' | 'denied' }
      ): void
      (action: 'set', key: string, value: string | number | boolean): void
      (action: 'event', eventName: string): void
    }
    heap: {
      identify: (userId: string) => void
      addUserProperties: (properties: Record<string, any>) => void
      track: (eventName: string, properties?: Record<string, any>) => void
      getUserId: () => string
      getIdentity: () => any
    }
    _hsq: Array<unknown[]>
  }
}

// Check if analytics platforms are available
const isClarityAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window.clarity &&
    typeof window.clarity === 'function'
  )
}

const isHeapAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    window.heap &&
    typeof window.heap.identify === 'function'
  )
}

// Enable GDPR consent (required for Clarity by Oct 31, 2025)
export const enableAnalyticsConsent = (consentGiven: boolean = true): void => {
  if (ANALYTICS_DISABLED) return
  if (!isClarityAvailable()) return

  try {
    window.clarity('consent', {
      analytics_storage: consentGiven ? 'granted' : 'denied',
    })
  } catch (error) {
    console.warn('Analytics consent setup failed:', error)
  }
}

// Main user identification function
export const identifyUser = (
  user: UserProperties,
  consentGiven: boolean = true
): void => {
  if (ANALYTICS_DISABLED) return
  const {
    userId,
    name,
    email,
    approvalStatus,
    preferredMarket,
    userType,
    signupDate,
    lastActiveDate,
    valueShareAmount,
    monthlyBudget,
  } = user

  // Enable consent first (GDPR compliance)
  enableAnalyticsConsent(consentGiven)

  // Microsoft Clarity identification
  if (isClarityAvailable()) {
    try {
      // Identify user with friendly name
      window.clarity(
        'identify',
        userId,
        undefined,
        undefined,
        name || email || userId
      )

      // Set user properties as custom dimensions
      if (approvalStatus) {
        window.clarity('set', 'approval_status', approvalStatus)
      }
      if (preferredMarket) {
        window.clarity('set', 'preferred_market', preferredMarket)
      }
      if (userType) {
        window.clarity('set', 'user_type', userType)
      }
      if (email) {
        window.clarity('set', 'user_email', email)
      }
      if (valueShareAmount) {
        window.clarity('set', 'value_share_amount', valueShareAmount)
      }
      if (monthlyBudget) {
        window.clarity('set', 'monthly_budget', monthlyBudget)
      }
    } catch (error) {
      console.warn('Clarity identification failed:', error)
    }
  }

  // Heap Analytics identification
  if (isHeapAvailable()) {
    try {
      // Identify user
      window.heap.identify(userId)

      // Add user properties
      const heapProperties: Record<string, any> = {}

      if (email) heapProperties.email = email
      if (name) heapProperties.name = name
      if (approvalStatus) heapProperties.approval_status = approvalStatus
      if (preferredMarket) heapProperties.preferred_market = preferredMarket
      if (userType) heapProperties.user_type = userType
      if (signupDate) heapProperties.signup_date = signupDate
      if (lastActiveDate) heapProperties.last_active_date = lastActiveDate
      if (valueShareAmount) heapProperties.value_share_amount = valueShareAmount
      if (monthlyBudget) heapProperties.monthly_budget = monthlyBudget

      // Add identification timestamp
      heapProperties.identified_at = new Date().toISOString()

      if (Object.keys(heapProperties).length > 0) {
        window.heap.addUserProperties(heapProperties)
      }
    } catch (error) {
      console.warn('Heap identification failed:', error)
    }
  }

  // HubSpot Analytics identification (enables Clarity-HubSpot session linking)
  if (typeof window !== 'undefined' && email) {
    try {
      window._hsq = window._hsq || []
      window._hsq.push([
        'identify',
        {
          email: email,
          firstname: name?.split(' ')[0] || '',
          lastname: name?.split(' ').slice(1).join(' ') || '',
        },
      ])
      window._hsq.push(['trackPageView'])
    } catch (error) {
      console.warn('HubSpot identification failed:', error)
    }
  }
}

// Update user properties (for when user state changes)
export const updateUserProperties = (
  updates: Partial<UserProperties>
): void => {
  if (ANALYTICS_DISABLED) return
  // Microsoft Clarity updates
  if (isClarityAvailable()) {
    try {
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert camelCase to snake_case for consistency
          const clarityKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
          window.clarity('set', clarityKey, value)
        }
      })
    } catch (error) {
      console.warn('Clarity property update failed:', error)
    }
  }

  // Heap Analytics updates
  if (isHeapAvailable()) {
    try {
      const heapUpdates: Record<string, any> = {}

      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Convert camelCase to snake_case for consistency
          const heapKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
          heapUpdates[heapKey] = value
        }
      })

      if (Object.keys(heapUpdates).length > 0) {
        heapUpdates.updated_at = new Date().toISOString()
        window.heap.addUserProperties(heapUpdates)
      }
    } catch (error) {
      console.warn('Heap property update failed:', error)
    }
  }
}

// Track approval status changes
export const trackApprovalStatusChange = (
  userId: string,
  oldStatus: string,
  newStatus: string
): void => {
  // Microsoft Clarity - track as custom event
  if (isClarityAvailable()) {
    try {
      window.clarity('event', 'approval_status_changed')
      window.clarity('set', 'approval_status', newStatus)
      window.clarity('set', 'previous_approval_status', oldStatus)
    } catch (error) {
      console.warn('Clarity approval tracking failed:', error)
    }
  }

  // Heap Analytics - track as event with properties
  if (isHeapAvailable()) {
    try {
      window.heap.track('approval_status_changed', {
        user_id: userId,
        old_status: oldStatus,
        new_status: newStatus,
        changed_at: new Date().toISOString(),
      })

      // Update user properties
      window.heap.addUserProperties({
        approval_status: newStatus,
        approval_status_updated_at: new Date().toISOString(),
      })
    } catch (error) {
      console.warn('Heap approval tracking failed:', error)
    }
  }
}

// Track property interactions (views, favorites, offers)
export const trackPropertyInteraction = (
  userId: string,
  propertyId: string,
  action: 'viewed' | 'favorited' | 'offer_created',
  additionalData?: Record<string, any>
): void => {
  const eventData = {
    user_id: userId,
    property_id: propertyId,
    action: action,
    timestamp: new Date().toISOString(),
    ...additionalData,
  }

  // Microsoft Clarity - simple event
  if (isClarityAvailable()) {
    try {
      window.clarity('event', `property_${action}`)
    } catch (error) {
      console.warn('Clarity property tracking failed:', error)
    }
  }

  // Heap Analytics - detailed event with properties
  if (isHeapAvailable()) {
    try {
      window.heap.track('property_interaction', eventData)
    } catch (error) {
      console.warn('Heap property tracking failed:', error)
    }
  }
}

// Reset user identification (for logout)
export const resetUserIdentification = (): void => {
  if (ANALYTICS_DISABLED) return
  // Microsoft Clarity doesn't have a reset method, but we can clear custom properties
  if (isClarityAvailable()) {
    try {
      // Clear user-specific properties
      const propertiesToClear = [
        'approval_status',
        'preferred_market',
        'user_type',
        'user_email',
        'value_share_amount',
        'monthly_budget',
      ]

      propertiesToClear.forEach((property) => {
        window.clarity('set', property, '')
      })
    } catch (error) {
      console.warn('Clarity reset failed:', error)
    }
  }

  // Heap Analytics - Note: Heap doesn't provide a way to "unidentify" users
  // The best practice is to simply not call identify() for subsequent anonymous users
  // New anonymous users will get new anonymous IDs automatically
}

// Debug helper to check if analytics are working
export const debugAnalytics = (): void => {
  console.group('🔍 Analytics Debug Info')
  console.log('Clarity available:', isClarityAvailable())
  console.log('Heap available:', isHeapAvailable())

  if (isHeapAvailable()) {
    try {
      console.log('Heap User ID:', window.heap.getUserId())
      console.log('Heap Identity:', window.heap.getIdentity())
    } catch (error) {
      console.log('Heap debug info unavailable:', error)
    }
  }
  console.groupEnd()
}

// All-in-one identification function for your auth flow
export const handleUserLogin = (
  user: UserProperties,
  consentGiven: boolean = true
): void => {
  if (ANALYTICS_DISABLED) return
  // Identify user in both platforms
  identifyUser(user, consentGiven)

  // Track login event
  if (isHeapAvailable()) {
    try {
      window.heap.track('user_login', {
        user_id: user.userId,
        login_time: new Date().toISOString(),
        approval_status: user.approvalStatus || 'unknown',
      })
    } catch (error) {
      console.warn('Login event tracking failed:', error)
    }
  }

  if (isClarityAvailable()) {
    try {
      window.clarity('event', 'user_login')
    } catch (error) {
      console.warn('Clarity login event failed:', error)
    }
  }
}
